/**
 * Market-aware pricing fixups.
 *
 * Volume tiers live in the `custom.volume_tiers` metafield as plain numbers in the
 * shop currency (USD). Shopify converts money fields (`variant.price`,
 * `selling_plan_allocation.*`) to the presentment currency, but never converts
 * metafield numbers, and Liquid exposes no exchange rate. Rendering
 * `variant.price - (reduction * 100)` therefore subtracts USD units from JPY/EUR/...
 * amounts (e.g. ¥5,268 - ¥3 instead of ¥5,268 - ¥479).
 *
 * This script re-computes every tier-derived amount with `Shopify.currency.rate`,
 * which is the same rate Shopify uses to convert the storefront prices. On the shop
 * currency the rate is 1 and nothing runs: the server-rendered markup is correct.
 */
(function () {
  'use strict';

  // Must stay in sync with the `data-money-sample` amount rendered in theme.liquid.
  var SAMPLE_CENTS = 111222333;
  var SAMPLE_UNIT_DIGITS = 7; // 111222333 cents -> 1112223 units

  var rate = parseFloat((window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || '1');
  if (!isFinite(rate) || rate <= 0 || rate === 1) return;

  var money = buildMoney(document.documentElement.getAttribute('data-money-sample'));
  if (!money) return;

  /**
   * Derives the active money format from a sample rendered by the `money` filter,
   * so JS output matches the Liquid-rendered prices around it.
   */
  function buildMoney(sample) {
    if (!sample) return null;

    var first = sample.search(/\d/);
    if (first === -1) return null;

    var last = first;
    for (var i = sample.length - 1; i >= first; i--) {
      if (sample.charAt(i) >= '0' && sample.charAt(i) <= '9') {
        last = i;
        break;
      }
    }

    var prefix = sample.slice(0, first);
    var suffix = sample.slice(last + 1);
    var numeric = sample.slice(first, last + 1);
    var decimals = numeric.replace(/\D/g, '').length - SAMPLE_UNIT_DIGITS;
    if (decimals < 0 || decimals > 3) decimals = 2;

    var decimalSep = decimals > 0 ? numeric.charAt(numeric.length - decimals - 1) : '';
    var integerPart = decimals > 0 ? numeric.slice(0, numeric.length - decimals - 1) : numeric;
    var groupMatch = integerPart.match(/\D/);
    var groupSep = groupMatch ? groupMatch[0] : '';
    // Smallest amount the active currency can display, in Liquid's 1/100 units.
    var step = Math.pow(10, 2 - decimals);

    function round(cents) {
      return Math.round(cents / step) * step;
    }

    function format(cents) {
      var value = (round(cents) / 100).toFixed(decimals);
      var negative = value.charAt(0) === '-';
      if (negative) value = value.slice(1);

      var parts = value.split('.');
      var integer = groupSep ? parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, groupSep) : parts[0];

      return (negative ? '-' : '') + prefix + integer + (parts[1] ? decimalSep + parts[1] : '') + suffix;
    }

    return { round: round, format: format };
  }

  /** Shop-currency amount (major units) -> presentment currency (1/100 units). */
  function convert(shopAmount) {
    return money.round(shopAmount * 100 * rate);
  }

  function toNumber(value) {
    var parsed = parseFloat(value);
    return isFinite(parsed) ? parsed : 0;
  }

  function refreshTiers(root) {
    var labels = root.querySelectorAll('[data-tier-reduction]');

    Array.prototype.forEach.call(labels, function (label) {
      var unitRegular = parseInt(label.getAttribute('data-tier-unit-price'), 10);
      var quantity = parseInt(label.getAttribute('data-tier-quantity'), 10);
      if (!isFinite(unitRegular) || !isFinite(quantity) || quantity < 1) return;

      var unitSpecial = unitRegular - convert(toNumber(label.getAttribute('data-tier-reduction')));
      var special = unitSpecial * quantity;
      var regular = unitRegular * quantity;

      var priceNode = label.querySelector('[data-tier-price]');
      if (priceNode) priceNode.textContent = money.format(unitSpecial);

      label.setAttribute('data-regular-price', money.format(regular));

      var percentNode = label.querySelector('[data-tier-save-percent]');
      if (percentNode && regular > 0) {
        var percent = Math.round(((regular - special) / regular) * 100);
        percentNode.textContent = (percentNode.getAttribute('data-template') || '[[percent]]').replace('[[percent]]', percent);
      }

      if (special === regular) {
        label.removeAttribute('data-special-price');
        label.removeAttribute('data-you-save');
        return;
      }

      var template = label.getAttribute('data-you-save-template') || '[[save]]';
      label.setAttribute('data-special-price', money.format(special));
      label.setAttribute('data-you-save', template.replace('[[save]]', money.format(regular - special)));
    });
  }

  function writeRow(summary, name, cents) {
    var node = summary.querySelector('[data-fx-row="' + name + '"]');
    if (!node) return;
    node.textContent = (node.getAttribute('data-fx-prefix') || '') + money.format(cents);
  }

  function refreshCartSummary(root) {
    var summary = root.querySelector('[data-fx-tiers]');
    if (!summary) return;

    var volumeDiscount = 0;
    var subscriptionVolumeDiscount = 0;

    (summary.getAttribute('data-fx-tiers') || '').split(',').forEach(function (entry) {
      if (!entry) return;
      var parts = entry.split(':');
      var lineDiscount = convert(toNumber(parts[0])) * (parseInt(parts[1], 10) || 0);
      volumeDiscount += lineDiscount;
      if (parts[2] === '1') subscriptionVolumeDiscount += lineDiscount;
    });

    var subscriptionDiscount = toNumber(summary.getAttribute('data-fx-subs-base')) - subscriptionVolumeDiscount;
    var subtotal = toNumber(summary.getAttribute('data-fx-original-total')) + volumeDiscount + subscriptionDiscount;
    var savings =
      toNumber(summary.getAttribute('data-fx-line-discount')) +
      toNumber(summary.getAttribute('data-fx-cart-discount')) +
      volumeDiscount +
      subscriptionDiscount;

    writeRow(summary, 'subtotal', subtotal);
    writeRow(summary, 'volume', volumeDiscount);
    writeRow(summary, 'subscription', subscriptionDiscount);
    writeRow(summary, 'savings', savings);

    var percentNode = summary.querySelector('[data-fx-row="savings-percent"]');
    if (percentNode && subtotal > 0) percentNode.textContent = Math.round((savings / subtotal) * 100);
  }

  /**
   * Standalone amounts derived from a shop-currency tier reduction, optionally
   * subtracted from a presentment-currency base (cart line item breakdown).
   */
  function refreshAmounts(root) {
    var nodes = root.querySelectorAll('[data-fx-shop-amount]');

    Array.prototype.forEach.call(nodes, function (node) {
      var converted = convert(toNumber(node.getAttribute('data-fx-shop-amount')));
      var base = node.getAttribute('data-fx-base');
      var cents = base === null ? converted : toNumber(base) - converted;
      node.textContent = (node.getAttribute('data-fx-prefix') || '') + money.format(cents);
    });
  }

  function refresh() {
    refreshTiers(document);
    refreshAmounts(document);
    refreshCartSummary(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refresh);
  } else {
    refresh();
  }

  // Cart and product sections are re-rendered server side, which restores the
  // shop-currency markup; re-apply once the new HTML is in the DOM.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS === 'object') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, refresh);
    subscribe(PUB_SUB_EVENTS.variantChange, refresh);
  }

  window.customMarketPrice = { refresh: refresh, convert: convert, format: money.format };
})();
